import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { Form, FormFieldSet } from '@epoch-ui/components/Form';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { SLACK_REVAMP } from 'constants/featureFlags';
import { useToaster } from 'hooks/useToaster';
import { ToasterVariant } from 'context/toasterContext';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import {
  updateSlackSettings,
  UpdateSlackSettingsInputType
} from '@event-editor/api/update-slack-settings';

import {
  getSlackSettingsSchema,
  SlackSettingsFormInput,
  SlackSettingsFormValues,
  convertDBToFormData
} from './slackSettingsSchema';
import { CreateNewChannelComponent } from './CreateNewChannelComponent';
import { ConnectExistingChannelComponent } from './ConnectExistingChannelComponent';
import { DisconnectSlackChannelModal } from './DisconnectSlackChannelModal';

interface EditSlackSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditSlackSettingsModal: React.FC<EditSlackSettingsModalProps> = ({
  isOpen,
  onClose
}) => {
  const event = useEditableEvent();
  const queryClient = useQueryClient();
  const isSlackRevampEnabled = useFeatureFlag(SLACK_REVAMP);
  const { showToast } = useToaster();
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);

  const methods = useForm<SlackSettingsFormInput>({
    resolver: zodResolver(
      getSlackSettingsSchema(
        !!isSlackRevampEnabled,
        event.datetimeScheduled,
        event.timezoneCode,
        event.slackChannelDate
      )
    ),
    values: convertDBToFormData(event, event.timezoneCode)
  });

  const {
    handleSubmit,
    formState: { isSubmitting }
  } = methods;

  // Check if slackChannelDate has passed (assuming it's stored in UTC)
  const isSlackChannelDatePassed = React.useMemo(() => {
    if (!event.slackChannelDate) return false;

    // Parse the UTC date string and compare with current UTC time
    // Ensure the date string is interpreted as UTC by appending 'Z' if not present
    const channelDate = new Date(`${event.slackChannelDate}Z`);
    const currentUTC = new Date();
    return channelDate <= currentUTC;
  }, [event.slackChannelDate]);

  const updateSettingsMutation = useMutation({
    mutationFn: (variables: UpdateSlackSettingsInputType) => updateSlackSettings(variables),
    onSuccess: (result: any, variables) => {
      queryClient.setQueryData(eventEditorQueryKeys.event(variables.eventId), (oldData: any) =>
        oldData
          ? {
              event: {
                ...oldData.event,
                ...result.updateSlackSettings.event
              }
            }
          : oldData
      );
      onClose();
    },
    onError: (error) => {
      console.error('Error updating Slack settings:', error);
      showToast('Failed to update Slack settings. Please try again.', {
        variant: ToasterVariant.ERROR
      });
    }
  });

  const onModalSubmit = (values: SlackSettingsFormValues): void => {
    if (!event.id) return;
    updateSettingsMutation.mutate({ eventId: event.id, ...values });
  };

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent size="sm" data-testid="edit-slack-settings-modal">
        <ModalHeader className="tw-mb-5">
          <ModalTitle>Slack Channel</ModalTitle>
          <ModalDescription className="tw-mt-2">
            {event.createSlackChannel
              ? 'You created a new Slack channel that guests will automatically join when they register.'
              : 'You connected an existing Slack channel that guests will automatically join when they register.'}
          </ModalDescription>
          <br />
          <ModalDescription className="tw-mt-0">
            To stop guests from joining this channel when they register or to connect a different
            channel, disconnect this channel.
          </ModalDescription>
        </ModalHeader>

        <Form {...methods}>
          <form
            onSubmit={handleSubmit(onModalSubmit, (errors) => {
              console.error('Slack settings validation failed:', errors);
              const firstError = Object.values(errors).find((error) => error?.message);
              showToast(
                String(firstError?.message ?? 'Please review the Slack channel settings.'),
                {
                  variant: ToasterVariant.ERROR
                }
              );
            })}
            className="tw-flex tw-flex-col"
          >
            <FormFieldSet>
              {event.createSlackChannel ? (
                <CreateNewChannelComponent disabled={isSlackChannelDatePassed} />
              ) : (
                <ConnectExistingChannelComponent disabled />
              )}
            </FormFieldSet>

            <ModalFooter className="tw-mt-5">
              <Button
                variant="destructive"
                onClick={(): void => setIsDisconnectModalOpen(true)}
                className="tw-w-full"
                size="sm"
              >
                Disconnect
              </Button>
              <Button
                size="sm"
                className="tw-h-10 tw-w-full tw-rounded-lg tw-bg-primary-blue tw-text-white"
                type="submit"
                disabled={updateSettingsMutation.isPending || isSubmitting}
              >
                Save
              </Button>
            </ModalFooter>
          </form>
        </Form>
      </ModalContent>
      <DisconnectSlackChannelModal
        isOpen={isDisconnectModalOpen}
        onClose={(): void => {
          setIsDisconnectModalOpen(false);
          onClose();
        }}
      />
    </Modal>
  );
};
